// Relative-but-precise stamps: a notification list is scanned, so the useful
// question is "how recent" first and "exactly when" second.
//
//   Today • 12:27
//   Yesterday • 18:04
//   31 Jul • 12:27      (this year)
//   12 Dec 2025 • 09:30 (older)
//
// The hour shown is the STORED WALL-CLOCK, rendered verbatim — a row written as
// `2026-07-31 14:39:30` reads `14:39`, on every machine, in every zone. No
// offset is ever applied, so the drawer agrees with the database and with the
// operations/preview screens, per the project-wide "UTC wall-clock end to end"
// rule. The `UTC` suffix is deliberately NOT printed: the reading is exact, and
// the label was noise.
//
// Only the calendar words and month names follow the viewer's locale; the digits
// never move.

/** Rendering zone. Fixed, not the runtime zone — see the note above. */
const DISPLAY_TIME_ZONE = 'UTC';

type FormatOptions = {
  /** Defaults to the runtime locale. Pinned in tests for determinism. */
  locales?: Intl.LocalesArgument;
  /**
   * Escape hatch for tests only. Production never passes this: shifting it
   * would make the drawer disagree with the stored value.
   */
  timeZone?: string;
};

const TIMEZONE_MARKER = /[zZ]$|[+-]\d{2}:?\d{2}$/;

/**
 * Naked Postgres strings ("2026-07-31 12:27:00") are UTC — say so explicitly,
 * or the browser would parse them as local time and shift the instant.
 */
const toInstant = (iso: string): Date | null => {
  if (!iso) return null;
  const normalised = TIMEZONE_MARKER.test(iso)
    ? iso
    : `${iso.replace(' ', 'T')}Z`;
  const date = new Date(normalised);
  return Number.isNaN(date.getTime()) ? null : date;
};

const format = (
  date: Date,
  { locales, timeZone = DISPLAY_TIME_ZONE }: FormatOptions,
  parts: Intl.DateTimeFormatOptions
): string =>
  new Intl.DateTimeFormat(locales, { timeZone, ...parts }).format(date);

const time = (date: Date, options: FormatOptions): string =>
  format(date, options, { hour: '2-digit', minute: '2-digit' });

const dayMonth = (date: Date, options: FormatOptions): string =>
  format(date, options, { day: 'numeric', month: 'short' });

const dayMonthYear = (date: Date, options: FormatOptions): string =>
  format(date, options, { day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Calendar days between two instants **in the display time zone**, so
 * "Today"/"Yesterday" agree with the hour printed beside them.
 *
 * Read off `en-CA` (which formats as `YYYY-MM-DD`) rather than `getFullYear()`
 * and friends, because those accessors answer in the MACHINE's zone and would
 * put a stamp on the wrong calendar day near midnight.
 */
const daysBetween = (
  later: Date,
  earlier: Date,
  { timeZone = DISPLAY_TIME_ZONE }: FormatOptions
): number => {
  const parse = (date: Date) => {
    const [y = 0, m = 1, d = 1] = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
      .format(date)
      .split('-')
      .map(Number);
    return Date.UTC(y, m - 1, d);
  };
  return Math.round((parse(later) - parse(earlier)) / 86_400_000);
};

const sameYear = (a: Date, b: Date, options: FormatOptions): boolean =>
  format(a, options, { year: 'numeric' }) ===
  format(b, options, { year: 'numeric' });

/** Compact stamp for the collapsed card. */
export const formatNotificationTime = (
  iso: string,
  now: Date = new Date(),
  options: FormatOptions = {}
): string => {
  const date = toInstant(iso);
  if (!date) return '';

  const days = daysBetween(now, date, options);
  if (days === 0) return `Today • ${time(date, options)}`;
  if (days === 1) return `Yesterday • ${time(date, options)}`;

  const day = sameYear(date, now, options)
    ? dayMonth(date, options)
    : dayMonthYear(date, options);
  return `${day} • ${time(date, options)}`;
};

/**
 * Unabbreviated stamp for the expanded card and screen-reader labels. Carries
 * no zone suffix — it is the stored value, printed as stored.
 */
export const formatNotificationTimeLong = (
  iso: string,
  options: FormatOptions = {}
): string => {
  const date = toInstant(iso);
  if (!date) return '';
  return `${dayMonthYear(date, options)} • ${time(date, options)}`;
};
